import React, { useEffect, useState, useRef } from 'react';
import './ProductList.css'; // Import the product styles
import Header from "./common/Header"; // Import the header
import { Link, useLocation, useSearchParams, matchPath } from 'react-router-dom';
import FilterModal from "./FilterModal";
import httpService from '../services/HttpService'; // Import your HttpService
import { formattedPrice } from '../utils/priceFormatter';
import useIsMobile from '../hooks/useIsMobile';

// TODO: reduce api calls(duplicates), reduce useEffect hooks, move filter modal to a separate component, use Redux for filters
const ProductList = ({fromProduct}) => {
    const isMobile = useIsMobile();
    const [products, setProducts] = useState([]);
    const [filters, setFilters] = useState([]); // available filters from backend
    const [categories, setCategories] = useState([]);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState([]);
    const [savedItems, setSavedItems] = useState({});
    const [offset, setOffset] = useState(0); // Track the offset for pagination
    const [isLoading, setIsLoading] = useState(false); // Loading state for fetching products
    const [hasMore, setHasMore] = useState(true); // Check if more products are available
    const filterRef = useRef(null);
    const limit = isMobile ? 20 : 50; // Number of products to fetch per request
    const [isClosing, setIsClosing] = useState(false);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const offsetRef = useRef(0); // 🔁 актуальный offset без задержек setState
    const [pendingFilters, setPendingFilters] = useState({});
    const [activeFilter, setActiveFilter] = useState(null);
    const [currentCat, setCurrentCat] = useState('')
    const [selectedSorting, setSelectedSorting] = useState(() => {
        return JSON.parse(localStorage.getItem("selectedSorting")) || null;
    });
    const countEnabledFilters = (search) => {
        const appliedFilters = []
        const params = new URLSearchParams(search);
        const sortKey = 'sorting_key'
        const availableFilters = filters.filters?.map(item => item.key).concat(sortKey) || []
        for (const [key] of params.entries()) {
            if (availableFilters.includes(key)){
                appliedFilters.push(key)
            }
        }
        return appliedFilters.length
    }

    const extractFiltersFromSearch = (search) => {
        const params = new URLSearchParams(search);
        const filterObj = {};

        for (const [key, value] of params.entries()) {
            if (["category_id", "offset", "limit", "sorting_direction"].includes(key)) continue;
            if (!filterObj[key]) filterObj[key] = [];
            filterObj[key].push(value);
        }

        return filterObj;
    };
    useEffect(()=>{
        if(location.search.includes('category_id')){
            setCurrentCat(location.search)
        }
    },[])
    function getScrollbarWidth() { //TODO: global
        const div = document.createElement("div");
        div.style.width = "100px";
        div.style.height = "100px";
        div.style.overflow = "scroll";
        document.body.appendChild(div);
        const scrollbarWidth = div.offsetWidth - div.clientWidth;
        document.body.removeChild(div);
        return scrollbarWidth;
    }

    const handleSortChange = (event) => {
        const [sorting_key, sorting_direction] = event.target.value.split("-");

        const selectedSort = filters.sorting.find(
            (sort) => sort.sorting_key === sorting_key && sort.sorting_direction === sorting_direction
        );

        const isSameSort =
            selectedSorting?.sorting_key === sorting_key &&
            selectedSorting?.sorting_direction === sorting_direction;

        if (isSameSort) {
            setSelectedSorting(null);
            localStorage.removeItem('selectedSorting');

            setPendingFilters(prev => {
                const updated = { ...prev };
                delete updated.sorting_key;
                delete updated.sorting_direction;
                return updated;
            });
        } else {
            setSelectedSorting(selectedSort);
            localStorage.setItem('selectedSorting', JSON.stringify(selectedSort));

            setPendingFilters(prev => ({
                ...prev,
                sorting_key: [sorting_key],
            }));
        }
    };

    // reset sorting and filters
    const resetSorting = () => {
        setHasMore(true);
        setSelectedSorting(null);
        setPendingFilters({})
        // const newParams = new URLSearchParams();
        // const categoryId = searchParams.get('category_id');
        // if (categoryId) newParams.set('category_id', categoryId);
        // setSearchParams(newParams);
        setOffset(0);
        offsetRef.current = 0;
        localStorage.removeItem('selectedSorting');
    };

    // Fetch categories based on the current parent category
    const fetchCategories = () => {
        const url = `/filters/suggestions` + location.search;
        httpService.get(url)
            .then(response => response.json())
            .then(data => {
                if (data?.length) {
                    setCategories(data);
                } else {
                    setCategories([]);
                }
            })
            .catch(error => console.error('Error fetching categories:', error));
    };

    const buildQueryString = (params) => {
        const excludedKeys = ['offset', 'limit', 'category_id', 'sorting_key', 'sorting_direction', 'price_min', 'price_max'];

        const queryArray = [];

        for (const [key, value] of params.entries()) {
            const finalKey = excludedKeys.includes(key) ? key : `${key}[]`;
            queryArray.push(`${encodeURIComponent(finalKey)}=${encodeURIComponent(value)}`);
        }

        return queryArray.join('&');
    };

    const fetchProducts = async (customOffset = null, reset = false, options = {}) => {
        const { silent = false, noSet = false } = options;
        
        if (!silent) setIsLoading(true);

        const currentOffset = customOffset !== null ? customOffset : offsetRef.current;
        const queryParams = new URLSearchParams(location.search);
        queryParams.set('offset', currentOffset);
        queryParams.set('limit', limit);

        const url = `/products?${buildQueryString(queryParams)}`;

        try {
            const response = await httpService.get(url);
            const data = await response.json();

            if (!noSet) {
                if (data.length > 0) {
                    setProducts(prev => reset ? data : [...prev, ...data]);
                    offsetRef.current = currentOffset + data.length;
                    setOffset(offsetRef.current);
                    setHasMore(data.length === limit);
                } else {
                    if (reset) {
                        setProducts([]);
                        offsetRef.current = 0;
                        setOffset(0);
                    }
                    setHasMore(false);
                }
            }

            return data;
        } catch (error) {
            console.error('Error fetching products:', error);
            return [];
        } finally {
            if (!silent) setIsLoading(false);
        }
    };
    const toggleSave = async (product) => {
        try {
            if (product.favourite?.id || savedItems[product.id]) {
                const favouriteId = product.favourite?.id || savedItems[product.id];
                const response = await httpService.delete(`/favourites/${favouriteId}`);
                const res = await response.json();
                if (!res.message) throw new Error("Failed to remove favorite");
                product.favourite = null;

                setSavedItems((prev) => {
                    const updated = { ...prev };
                    delete updated[product.id];
                    return updated;
                });

            } else {
                const response = await httpService.post("/favourites", { product_id: product.id });
                const res = await response.json();
                if (res?.id) {
                    setSavedItems((prev) => ({
                        ...prev,
                        [product.id]: res.id,
                    }));
                } else {
                    throw new Error("Invalid response from server");
                }
            }
        } catch (error) {
            console.error("Error toggling favorite:", error);
        }
    };

    const handlePriceChange = (key, value) => {
        setPendingFilters(prev => {
            const updated = { ...prev };
            if (!value || value === "") {
                delete updated[key];
            } else {
                updated[key] = [value]; // Оборачиваем в массив, как и другие фильтры
            }
            return updated;
        });
    };

    const handleSuggestionSelect = (filterKey, suggestionValue) => {
        setPendingFilters(prev => {
            const currentValues = prev[filterKey] || [];
            const isSelected = currentValues.includes(suggestionValue);
            const updated = isSelected
                ? currentValues.filter(v => v !== suggestionValue)
                : [...currentValues, suggestionValue];

            return {
                ...prev,
                [filterKey]: updated
            };
        });
    };

    const applyFilters = () => {
        const newParams = new URLSearchParams();

        const categoryId = searchParams.get('category_id');
        if (categoryId) newParams.set('category_id', categoryId);

        Object.entries(pendingFilters).forEach(([key, values]) => {
            values.forEach(v => newParams.append(key, v));
        });

        setSearchParams(newParams);
        setSidebarOpen(false);
        document.body.classList.remove("sidebar-open");
        setOffset(0);
    };

    const handleTagClick = (tag, arrayName) => {
        setActiveTags((prevActiveTags) => {
            const updatedTags = prevActiveTags.includes(tag)
                ? prevActiveTags.filter((t) => t !== tag)
                : [...prevActiveTags, tag];

            return updatedTags;
        });

        const currentValues = extractFiltersFromSearch(location.search)?.[arrayName] || [];
        const isSelected = currentValues.includes(tag);

        const newParams = new URLSearchParams(searchParams);
        newParams.delete(arrayName);

        const updatedValues = isSelected
            ? currentValues.filter((v) => v !== tag)
            : [...currentValues, tag];

        updatedValues.forEach((v) => newParams.append(arrayName, v));

        setSearchParams(newParams);
        setOffset(0);
    };


    const closeModal = () => {
        setIsClosing(true);
        setTimeout(() => {
            setActiveFilter(null)
            setIsClosing(false);
        }, 200);
    };

    const toggleSidebar = () => {
        closeModal();
        setSidebarOpen(!isSidebarOpen);
        document.body.classList.toggle('sidebar-open', !isSidebarOpen);
    };

    const [activeTags, setActiveTags] = useState([]);

    const handleFilterToggle = (category) => {
        if (isLoading) return;

        const key = category.key;
        const value = category.value;
        const currentValues = extractFiltersFromSearch(location.search)?.[key] || [];
        const isSelected = currentValues.includes(value);

        const newParams = new URLSearchParams(searchParams);
        newParams.delete(key);

        const updatedValues = isSelected
            ? currentValues.filter(v => v !== value)
            : [...currentValues, value];

        updatedValues.forEach(v => newParams.append(key, v));

        setSearchParams(newParams);
        setOffset(0);

        setProducts([])
    };

    useEffect(() => {
        const isDiff = currentCat !== location.search
        if ((location.search.includes('category_id') && !fromProduct) || (isDiff && fromProduct)) {
            window.scrollTo(0, 0);
            fetchProducts(0, true, { noSet: false });
            setCurrentCat(location.search)
        }
        setPendingFilters(extractFiltersFromSearch(location.search));
    }, [location.search]);

    // desktop
    useEffect(() => {
        function handleClickOutside(event) {
            const clickedFilterButton = event.target.closest('.filter-categories-container');
            if (filterRef.current && !filterRef.current.contains(event.target) && !clickedFilterButton) {
                setSidebarOpen(false);
                document.body.classList.remove('sidebar-open');
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Scrollbar, seems like no issues here
    useEffect(() => {
        const scrollbarWidth = getScrollbarWidth();

        if (isSidebarOpen) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
            document.querySelector('.desktop-header')?.classList.remove('shadow');
        } else {
            document.body.style.paddingRight = "0px";
            document.querySelector('.desktop-header')?.classList.add('shadow');
        }
    }, [isSidebarOpen]);

    useEffect(() => {
        httpService.get('/filters' + location.search)
            .then(response => response.json())
            .then(data => setFilters(data))
            .then(() => fetchCategories())
            .catch(error => console.error('Error fetching filters:', error));
    }, []);

    // скролл
    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + document.documentElement.scrollTop >= document.documentElement.scrollHeight -100 &&
                !isLoading &&
                hasMore
                && offsetRef.current > 0
            ) {
                fetchProducts();
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [isLoading, hasMore]);

    return (
        <>
            <div className="products-listing">
                <Header />
                {isLoading && (
                    <div className="zametna-loader-centered">zametna</div>
                )}
                <div className="filter-categories-container">
                    <div style={{position: 'relative'}}>
                        <button className="filter-button" onClick={toggleSidebar}>
                            <span className="material-symbols-sharp">{isSidebarOpen ? 'chevron_left' : 'filter_list'}</span>
                        </button>
                        {countEnabledFilters(location.search) > 0 && (<span className='filter-count'>{countEnabledFilters(location.search)}</span>)}
                    </div>
                    {isSidebarOpen && (
                        <button className="reset-button" onClick={resetSorting}>Сбросить</button>
                    )}
                    {!isSidebarOpen &&
                        <div className="categories-scroll">
                            {categories.length === 0 ? (
                                <p>No categories available</p>
                            ) : (
                                categories.map((category) => {
                                    const isSelected =
                                        extractFiltersFromSearch(location.search)?.[category.key] &&
                                        extractFiltersFromSearch(location.search)?.[category.key].includes(category.value);

                                    return (
                                        <button
                                            key={`${category.key}-${category.value}`}
                                            className={`category-button ${isSelected ? "selected" : ""}`}
                                            onClick={() => handleFilterToggle(category)}
                                        >
                                            {category.name}
                                            {isSelected && (
                                                <span
                                                    className="remove-icon"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleFilterToggle(category);
                                                    }}
                                                >
                                                    ×
                                                </span>
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    }
                </div>
            </div>

            <div ref={filterRef} className={`filter-container ${isSidebarOpen ? 'open' : ''}`} style={{ touchAction: activeFilter ? "none" : "", overflow: activeFilter ? "hidden" : "auto" }}>
                {/* Filter sections remain unchanged */}
                <div className="filter-section">
                    <h3>Сортировать</h3>
                    <div className="checkbox-group">
                        {filters && filters.sorting?.map((sort, index) => (
                            <label key={index}>
                                <input
                                    type="radio"
                                    name="sorting"
                                    value={`${sort.sorting_key}-${sort.sorting_direction}`}
                                    checked={
                                        selectedSorting?.sorting_key === sort.sorting_key &&
                                        selectedSorting?.sorting_direction === sort.sorting_direction
                                    }
                                    onChange={handleSortChange}
                                    onClick={(event) => {
                                        if (
                                            selectedSorting?.sorting_key === sort.sorting_key &&
                                            selectedSorting?.sorting_direction === sort.sorting_direction
                                        ) {
                                            setSelectedSorting(null);
                                            localStorage.removeItem('selectedSorting');
                                            event.target.checked = false;
                                            handleSortChange(event);
                                        }
                                    }}
                                />
                                {sort.name}
                            </label>
                        ))}
                    </div>
                </div>
                {filters && filters.filters?.map((filter, index) => {
                    if (filter.type === "multiselect") {
                        const isNextInputPrice = filters.filters[index - 1]?.key === "price_max";
                        return (
                            <div key={filter.key} className={`filter-section ${isNextInputPrice ? 'margin-top-80 border-top' : ''}`}>
                                <div className="filter-header" onClick={() => setActiveFilter(filter)}>
                                    <p className="filter-title">{filter.name}</p>
                                    <span className="material-symbols-sharp">chevron_right</span>
                                </div>
                                {filter.suggestions.length > 0 && (
                                    <div className="suggestions-container">
                                        {filter.suggestions.map((suggestion) => (
                                            <button
                                                key={suggestion.value}
                                                className={`suggestion-button ${pendingFilters?.[filter.key]?.includes(suggestion.value) ? 'selected' : ''}`}
                                                onClick={() => handleSuggestionSelect(filter.key, suggestion.value)}
                                            >
                                                {
                                                    filter.key === "color" && (
                                                        <div
                                                            className="color-swatch"
                                                            style={{ backgroundColor: suggestion.hex }}
                                                        ></div>
                                                    )
                                                }
                                                {suggestion.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }

                    if (filter.type === "input") {
                        return (
                            <div key={filter.key} className={`price-range ${filter.key === "price_max" ? 'right-10' : ''}`}>
                                <input
                                    className='price-range-input'
                                    type="number"
                                    placeholder={filter.name}
                                    value={pendingFilters?.[filter.key] || ""}
                                    onChange={(e) => handlePriceChange(filter.key, e.target.value)}
                                />
                            </div>
                        );
                    }

                    return null;
                })}

                {activeFilter && (
                    <div className={`modal-wrapper ${isClosing ? "fade-out" : "fade-in"}`}>
                        <div className={`filter-modal ${activeFilter ? "activeFilter" : ""}`} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <button onClick={closeModal}>
                                    <span className="material-symbols-sharp">chevron_left</span>
                                </button>
                                <div>{activeFilter.name}</div>
                            </div>
                            <div className="filter-modal-content">
                                {activeFilter.options.map((option, index) => (
                                    <label key={index} className="filter-option">
                                        {option.name}
                                        <input
                                            type="checkbox"
                                            value={option.value}
                                            checked={pendingFilters?.[activeFilter.key]?.includes(option.value) || false}
                                            onChange={() => handleSuggestionSelect(activeFilter.key, option.value)}
                                        />
                                    </label>
                                ))}
                            </div>
                            <button className="apply-button" onClick={() => setActiveFilter(null)}>Сохранить</button>
                        </div>
                    </div>
                )}

                <FilterModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data={modalData} activeTags={activeTags} handleTagClick={handleTagClick} />
                <button className="view-all-button" onClick={() => applyFilters()}>Применить</button>
            </div>

            <div className="product-list-container">
                {isLoading && <div className="msg"></div>}
                {products.length === 0 && !isLoading ? (
                    <div className="msg">No products found.</div>
                ) : (
                    products.map((product, index) => (
                        <div
                            className="product-list-card"
                            key={product.id}
                        >
                            <Link
                                className="link"
                                to={`/product/${product.id}`}
                                onClick={() => {
                                    sessionStorage.setItem('scrollPosition', window.pageYOffset.toString());
                                }}
                            >
                                <div className="product-list-image">
                                    <img src={product.main_image?.link} alt={product.name} />
                                </div>
                            </Link>
                            <div className="product-list-details">
                                <div className="product-list-name">{product.brand.name}</div>
                                <div className="product-list-description">{product.name}</div>
                                <p className="product-list-price">
                                    {formattedPrice(product.price, product.actual_price, product.sale)}
                                </p>
                            </div>
                            <button className="product-list-favorite-icon" onClick={() => toggleSave(product)}>
                                <span
                                    className="material-symbols-sharp"
                                    style={{
                                        fontVariationSettings: `'FILL' ${product.favourite?.id || savedItems[product.id] ? 1 : 0}, 'wght' 400, 'GRAD' 0, 'opsz' 24`,
                                        transition: "font-variation-settings 0.3s ease-in-out",
                                        cursor: "pointer",
                                    }}
                                >
                                    bookmark
                                </span>
                            </button>
                        </div>
                    ))
                )}
                {isSidebarOpen && <div style={{left:'40%', top: '157px'}} className="sidebar-overlay"></div>}
            </div>
        </>
    );
};

export default ProductList;